import { freshModal } from "../../components/freshModal";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "../../components/reusableComponents/Input";
import { Label } from "../../components/reusableComponents/Label";
import LazyLoadingSelectQualification from "../../components/reusableComponents/LazyLoadingSelectQualification";
import qualifications from "../../utils/qualifications";
import { message } from "antd";
import axios from "../../axios/AxiosInstance";
import { User, Mail, Phone, Upload, X, Building, Award, FileText, Plus, Check, MapPin, Calendar, Shield, Car, DollarSign, Clock, Users, Heart, Briefcase } from "lucide-react";


function CreateCandidateModalFormSession({
  open,
  onOpenChange,
  candidates,
  setCandidates,
}) {
  const [timer, setTimer] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [isLoadingClient, setIsLoadingClient] = useState(false);
  const [clients, setClients] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);


  const inputFileRef = useRef(null);
  const clientRef = useRef(null);
  const dropdownRef = useRef(null);

  // Constantes pour l'optimisation
  const MAX_VISIBLE_CLIENTS = 50; // Limite d'affichage pour éviter les lags
  const SEARCH_DEBOUNCE_MS = 300;

  const [formData, setFormData] = useState({
    // Informations personnelles
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    phone2: "",
    address: "",
    postCode: "",
    town: "",
    dob: "",
    nationality: "",
    birthCountry: "",
    birthTown: "",
    birthDepartment: "",
    securityNumber: "",

    // Contact d'urgence
    urgentContactName: "",
    urgentContactPhone: "",

    // Informations professionnelles
    qualification: "", // Pour LazyLoadingSelectQualification
    qualifications: [], // Pour l'API (array)
    contratType: "",
    status: "Disponible",

    // Compétences et certifications
    licences: [],
    diplomas: [],
    habilitations: [],
    caceses: [],
    epis: [],
    locomotions: [],

    // Préférences
    availabilities: [],
    zones: [],
    distance: "",
    dailySalaryExpectations: "",
    monthlySalaryExpectations: "",
    yearlySalaryExpectations: "",

    // Autres
    isRQTH: false,
    comment: "",
    clients: [],
  });

  // Gestion des clics extérieurs optimisée
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        clientRef.current &&
        !clientRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setShowClientDropdown(false);
      }
    };

    if (showClientDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showClientDropdown]);

  // Clients filtrés et optimisés avec useMemo
  const filteredClients = useMemo(() => {
    if (!clientSearch || clientSearch.length < 3) return [];
    return clients.slice(0, MAX_VISIBLE_CLIENTS); // Limite pour éviter les lags
  }, [clients, clientSearch]);

  // Fonction de sélection/désélection optimisée
  const toggleClientSelection = useCallback((client) => {
    setFormData(prev => {
      const alreadySelected = prev.clients?.some((c) => c._id === client._id);
      const updatedClients = alreadySelected
        ? prev.clients.filter((c) => c._id !== client._id)
        : [...prev.clients, { _id: client._id, name: client.companyName }];

      return { ...prev, clients: updatedClients };
    });
  }, []);

  // Fonction de recherche optimisée avec useCallback
  const searchClient = useCallback((word) => {
    setClientSearch(word);

    if (word.length === 0) {
      setClients([]);
      setShowClientDropdown(false);
      return;
    }

    if (word.length < 3) {
      if (timer) clearTimeout(timer);
      setShowClientDropdown(false);
      return;
    }

    if (timer) clearTimeout(timer);

    const timer2 = setTimeout(() => {
      setIsLoadingClient(true);
      axios
        .post("client/freelance/find-by-text", { query: word })
        .then((res) => {
          setClients(res.data);
          setShowClientDropdown(true);
        })
        .catch((e) => {
          console.error("Erreur recherche clients:", e);
          setClients([]);
        })
        .finally(() => setIsLoadingClient(false));
    }, SEARCH_DEBOUNCE_MS);

    setTimer(timer2);
  }, [timer]);



  const addCandidate = (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    if (!isFormComplete()) {
      message.error("Veuillez remplir tous les champs!");
      setLoading(false);
      return;
    }

    const newPhone = formData.phone.replaceAll(" ", "").replaceAll(".", "");

    // Préparation des qualifications pour l'API
    const qualificationsArray = formData.qualification ? [formData.qualification] : [];

    // Création de l'objet FormData
    const formPayload = new FormData();

    // Informations de base (obligatoires)
    formPayload.append("firstname", formData.firstname);
    formPayload.append("lastname", formData.lastname);
    formPayload.append("email", formData.email);
    formPayload.append("phone", newPhone);

    // Qualifications (format attendu par l'API)
    formPayload.append("qualifications", qualificationsArray.join(","));

    // Clients sélectionnés
    if (formData.clients && formData.clients.length > 0) {
      formPayload.append("clients", formData.clients.map((client) => client._id));
    }

    // Informations complémentaires (optionnelles)
    if (formData.phone2) formPayload.append("phone2", formData.phone2);
    if (formData.address) formPayload.append("address", formData.address);
    if (formData.postCode) formPayload.append("postCode", formData.postCode);
    if (formData.town) formPayload.append("town", formData.town);
    if (formData.dob) formPayload.append("dob", formData.dob);
    if (formData.nationality) formPayload.append("nationality", formData.nationality);
    if (formData.birthCountry) formPayload.append("birthCountry", formData.birthCountry);
    if (formData.birthTown) formPayload.append("birthTown", formData.birthTown);
    if (formData.birthDepartment) formPayload.append("birthDepartment", formData.birthDepartment);
    if (formData.securityNumber) formPayload.append("securityNumber", formData.securityNumber);
    if (formData.urgentContactName) formPayload.append("urgentContactName", formData.urgentContactName);
    if (formData.urgentContactPhone) formPayload.append("urgentContactPhone", formData.urgentContactPhone);
    if (formData.contratType) formPayload.append("contratType", formData.contratType);
    if (formData.status) formPayload.append("status", formData.status);
    if (formData.distance) formPayload.append("distance", formData.distance);
    if (formData.dailySalaryExpectations) formPayload.append("dailySalaryExpectations", formData.dailySalaryExpectations);
    if (formData.comment) formPayload.append("comment", formData.comment);

    // Booléens
    formPayload.append("isRQTH", formData.isRQTH.toString());

    // fichier
    if (selectedFile) {
      formPayload.append("file", selectedFile); // 'file' doit correspondre à ce que le backend attend
    }

    setTimeout(() => {
      for (let [key, value] of formPayload.entries()) {
        console.log(`${key}:`, value);
      }
    }, 2000);

    axios
      .post("tempworker-candidate", formPayload, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => {
        const newCandidates = [...candidates, res.data];
        setCandidates(newCandidates);
        setLoading(false);
        onOpenChange(false);
      })
      .catch((e) => {
        setLoading(false);
        console.error("Erreur lors de l'envoi:", e.response?.data?.message || e.message);
      });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file.type === "application/pdf") {
      setSelectedFile(event.target.files[0]);
      //analyzeCV(event.target.files[0]);
    } else {
      message.error("Veuillez ajouter un fichier pdf !");
    }
  };

  const handleClick = () => {
    inputFileRef.current.click();
  };

  const isFormComplete = () => {
    const { firstname, lastname, email, phone } = formData;
    return firstname !== "" && lastname !== "" && email !== "" && phone !== "";
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => onOpenChange(false)}
        >
          <motion.div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header avec gradient moderne */}
            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-8 py-6 text-white relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-sm"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <motion.div
                    className="bg-white/20 backdrop-blur-sm rounded-xl p-3"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, duration: 0.3 }}
                  >
                    <User className="w-6 h-6 text-white" />
                  </motion.div>
                  <div>
                    <motion.h2
                      className="text-2xl font-bold"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3, duration: 0.3 }}
                    >
                      Nouveau candidat
                    </motion.h2>
                    <motion.p
                      className="text-blue-100 text-sm"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4, duration: 0.3 }}
                    >
                      Créez un nouveau profil candidat
                    </motion.p>
                  </div>
                </div>
                <motion.button
                  onClick={() => onOpenChange(false)}
                  className="text-white/80 hover:text-white hover:bg-white/20 rounded-lg p-2 transition-all duration-200"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <X className="w-5 h-5" />
                </motion.button>
              </div>
            </div>

            {/* Contenu du formulaire */}
            <div className="flex-1 overflow-y-auto max-h-[calc(90vh-120px)]">
              <form onSubmit={addCandidate} className="p-8 space-y-6">
                {/* Section Informations personnelles */}
                <motion.div
                  className="space-y-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.3 }}
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="bg-blue-100 rounded-lg p-2">
                      <User className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Informations personnelles</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Prénom */}
                    <div className="space-y-2">
                      <Label htmlFor="firstname" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                        <User className="w-4 h-4 text-gray-500" />
                        <span>Prénom</span>
                      </Label>
                      <Input
                        id="firstname"
                        value={formData.firstname}
                        onChange={(e) => setFormData({ ...formData, firstname: e.target.value })}
                        placeholder="Prénom du candidat"
                        className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-lg transition-all duration-200"
                      />
                    </div>

                    {/* Nom */}
                    <div className="space-y-2">
                      <Label htmlFor="lastname" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                        <User className="w-4 h-4 text-gray-500" />
                        <span>Nom</span>
                      </Label>
                      <Input
                        id="lastname"
                        value={formData.lastname}
                        onChange={(e) => setFormData({ ...formData, lastname: e.target.value })}
                        placeholder="Nom du candidat"
                        className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-lg transition-all duration-200"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Email */}
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                        <Mail className="w-4 h-4 text-gray-500" />
                        <span>Email</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="email@exemple.com"
                        className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-lg transition-all duration-200"
                      />
                    </div>

                    {/* Téléphone */}
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                        <Phone className="w-4 h-4 text-gray-500" />
                        <span>Téléphone</span>
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="06 12 34 56 78"
                        className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-lg transition-all duration-200"
                      />
                    </div>

                    {/* Téléphone 2 */}
                    <div className="space-y-2">
                      <Label htmlFor="phone2" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                        <Phone className="w-4 h-4 text-gray-500" />
                        <span>Téléphone 2 (optionnel)</span>
                      </Label>
                      <Input
                        id="phone2"
                        type="tel"
                        value={formData.phone2}
                        onChange={(e) => setFormData({ ...formData, phone2: e.target.value })}
                        placeholder="06 12 34 56 78"
                        className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-lg transition-all duration-200"
                      />
                    </div>

                    {/* Date de naissance */}
                    <div className="space-y-2">
                      <Label htmlFor="dob" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <span>Date de naissance</span>
                      </Label>
                      <Input
                        id="dob"
                        type="date"
                        value={formData.dob}
                        onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                        className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-lg transition-all duration-200"
                      />
                    </div>

                    {/* Nationalité */}
                    <div className="space-y-2">
                      <Label htmlFor="nationality" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                        <Users className="w-4 h-4 text-gray-500" />
                        <span>Nationalité</span>
                      </Label>
                      <Input
                        id="nationality"
                        value={formData.nationality}
                        onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                        placeholder="Française"
                        className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-lg transition-all duration-200"
                      />
                    </div>
                  </div>
                </motion.div>

                {/* Section Adresse */}
                <motion.div
                  className="space-y-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.3 }}
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="bg-green-100 rounded-lg p-2">
                      <MapPin className="w-5 h-5 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Adresse</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Adresse */}
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="address" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                        <MapPin className="w-4 h-4 text-gray-500" />
                        <span>Adresse</span>
                      </Label>
                      <Input
                        id="address"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="123 rue de la République"
                        className="border-gray-300 focus:border-green-500 focus:ring-green-500 rounded-lg transition-all duration-200"
                      />
                    </div>

                    {/* Code postal */}
                    <div className="space-y-2">
                      <Label htmlFor="postCode" className="text-sm font-medium text-gray-700">
                        Code postal
                      </Label>
                      <Input
                        id="postCode"
                        value={formData.postCode}
                        onChange={(e) => setFormData({ ...formData, postCode: e.target.value })}
                        placeholder="75001"
                        className="border-gray-300 focus:border-green-500 focus:ring-green-500 rounded-lg transition-all duration-200"
                      />
                    </div>

                    {/* Ville */}
                    <div className="space-y-2">
                      <Label htmlFor="town" className="text-sm font-medium text-gray-700">
                        Ville
                      </Label>
                      <Input
                        id="town"
                        value={formData.town}
                        onChange={(e) => setFormData({ ...formData, town: e.target.value })}
                        placeholder="Paris"
                        className="border-gray-300 focus:border-green-500 focus:ring-green-500 rounded-lg transition-all duration-200"
                      />
                    </div>
                  </div>
                </motion.div>

                {/* Section Qualifications avec LazyLoading */}
                <motion.div
                  className="space-y-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.3 }}
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="bg-purple-100 rounded-lg p-2">
                      <Award className="w-5 h-5 text-purple-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Qualification</h3>
                  </div>

                  <div className="space-y-2">
                    <LazyLoadingSelectQualification
                      qualifications={qualifications}
                      formData={formData}
                      setFormData={setFormData}
                    />
                    {formData.qualification && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.2 }}
                        className="bg-purple-50 w-fit mt-2 text-purple-600 px-3 py-1 rounded-md text-sm italic flex items-center space-x-2"
                      >
                        <Award className="w-3 h-3" />
                        <span>{formData.qualification}</span>
                      </motion.div>
                    )}
                  </div>
                </motion.div>

                {/* Section Informations de naissance */}
                <motion.div
                  className="space-y-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.65, duration: 0.3 }}
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="bg-orange-100 rounded-lg p-2">
                      <Users className="w-5 h-5 text-orange-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Informations de naissance</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Pays de naissance */}
                    <div className="space-y-2">
                      <Label htmlFor="birthCountry" className="text-sm font-medium text-gray-700">
                        Pays de naissance
                      </Label>
                      <Input
                        id="birthCountry"
                        value={formData.birthCountry}
                        onChange={(e) => setFormData({ ...formData, birthCountry: e.target.value })}
                        placeholder="France"
                        className="border-gray-300 focus:border-orange-500 focus:ring-orange-500 rounded-lg transition-all duration-200"
                      />
                    </div>

                    {/* Ville de naissance */}
                    <div className="space-y-2">
                      <Label htmlFor="birthTown" className="text-sm font-medium text-gray-700">
                        Ville de naissance
                      </Label>
                      <Input
                        id="birthTown"
                        value={formData.birthTown}
                        onChange={(e) => setFormData({ ...formData, birthTown: e.target.value })}
                        placeholder="Paris"
                        className="border-gray-300 focus:border-orange-500 focus:ring-orange-500 rounded-lg transition-all duration-200"
                      />
                    </div>

                    {/* Département de naissance */}
                    <div className="space-y-2">
                      <Label htmlFor="birthDepartment" className="text-sm font-medium text-gray-700">
                        Département de naissance
                      </Label>
                      <Input
                        id="birthDepartment"
                        value={formData.birthDepartment}
                        onChange={(e) => setFormData({ ...formData, birthDepartment: e.target.value })}
                        placeholder="75"
                        className="border-gray-300 focus:border-orange-500 focus:ring-orange-500 rounded-lg transition-all duration-200"
                      />
                    </div>
                  </div>

                  {/* Numéro de sécurité sociale */}
                  <div className="space-y-2">
                    <Label htmlFor="securityNumber" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                      <Shield className="w-4 h-4 text-gray-500" />
                      <span>Numéro de sécurité sociale</span>
                    </Label>
                    <Input
                      id="securityNumber"
                      value={formData.securityNumber}
                      onChange={(e) => setFormData({ ...formData, securityNumber: e.target.value })}
                      placeholder="1 23 45 67 890 123 45"
                      className="border-gray-300 focus:border-orange-500 focus:ring-orange-500 rounded-lg transition-all duration-200"
                    />
                  </div>

                  {/* RQTH */}
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="isRQTH"
                      checked={formData.isRQTH}
                      onChange={(e) => setFormData({ ...formData, isRQTH: e.target.checked })}
                      className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <Label htmlFor="isRQTH" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                      <Heart className="w-4 h-4 text-gray-500" />
                      <span>Reconnaissance de la Qualité de Travailleur Handicapé (RQTH)</span>
                    </Label>
                  </div>
                </motion.div>

                {/* Section Contact d'urgence */}
                <motion.div
                  className="space-y-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.68, duration: 0.3 }}
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="bg-red-100 rounded-lg p-2">
                      <Phone className="w-5 h-5 text-red-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Contact d'urgence</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Nom du contact d'urgence */}
                    <div className="space-y-2">
                      <Label htmlFor="urgentContactName" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                        <User className="w-4 h-4 text-gray-500" />
                        <span>Nom du contact</span>
                      </Label>
                      <Input
                        id="urgentContactName"
                        value={formData.urgentContactName}
                        onChange={(e) => setFormData({ ...formData, urgentContactName: e.target.value })}
                        placeholder="Nom Prénom"
                        className="border-gray-300 focus:border-red-500 focus:ring-red-500 rounded-lg transition-all duration-200"
                      />
                    </div>

                    {/* Téléphone du contact d'urgence */}
                    <div className="space-y-2">
                      <Label htmlFor="urgentContactPhone" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                        <Phone className="w-4 h-4 text-gray-500" />
                        <span>Téléphone du contact</span>
                      </Label>
                      <Input
                        id="urgentContactPhone"
                        type="tel"
                        value={formData.urgentContactPhone}
                        onChange={(e) => setFormData({ ...formData, urgentContactPhone: e.target.value })}
                        placeholder="06 12 34 56 78"
                        className="border-gray-300 focus:border-red-500 focus:ring-red-500 rounded-lg transition-all duration-200"
                      />
                    </div>
                  </div>
                </motion.div>

                {/* Section Clients */}
                <motion.div
                  className="space-y-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7, duration: 0.3 }}
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="bg-green-100 rounded-lg p-2">
                      <Building className="w-5 h-5 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Clients concernés</h3>
                  </div>

                  <div ref={clientRef} className="space-y-3 relative">
                    <Label htmlFor="client" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                      <Building className="w-4 h-4 text-gray-500" />
                      <span>Rechercher et sélectionner les clients</span>
                    </Label>

                    <div className="relative">
                      <Input
                        value={clientSearch}
                        onChange={(e) => searchClient(e.target.value)}
                        placeholder="Tapez le nom du client..."
                        className="border-gray-300 focus:border-green-500 focus:ring-green-500 rounded-lg transition-all duration-200 pr-10"
                      />
                      {isLoadingClient && (
                        <div className="absolute inset-y-0 right-3 flex items-center">
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full"
                          />
                        </div>
                      )}

                      {/* Dropdown positionné correctement */}
                      {showClientDropdown && filteredClients.length > 0 && (
                        <motion.div
                          ref={dropdownRef}
                          className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-hidden"
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          {/* Header avec compteur */}
                          {clients.length > MAX_VISIBLE_CLIENTS && (
                            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm text-gray-600">
                              Affichage de {filteredClients.length} sur {clients.length} résultats
                            </div>
                          )}

                          {/* Liste scrollable optimisée */}
                          <div className="overflow-y-auto max-h-48">
                            {filteredClients.map((client, index) => (
                              <motion.div
                                key={client._id}
                                onClick={() => toggleClientSelection(client)}
                                className={`px-4 py-3 cursor-pointer transition-all duration-150 flex items-center space-x-3 border-b border-gray-100 last:border-b-0 ${
                                  formData.clients?.some((c) => c._id === client._id)
                                    ? "bg-green-50 border-l-4 border-green-500 font-medium text-green-800"
                                    : "hover:bg-gray-50"
                                }`}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.02, duration: 0.2 }}
                                whileHover={{ x: 4 }}
                              >
                                <Building className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                <span className="truncate flex-1">{client.companyName}</span>
                                {formData.clients?.some((c) => c._id === client._id) && (
                                  <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                                )}
                              </motion.div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </div>

                    {formData.clients.length > 0 && (
                      <motion.div
                        className="flex flex-wrap gap-2 mt-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                      >
                        {formData.clients.map((client, index) => (
                          <motion.span
                            key={client._id}
                            className="flex items-center bg-gradient-to-r from-green-100 to-green-50 text-green-800 text-sm font-medium px-3 py-2 rounded-full border border-green-200"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: index * 0.1 }}
                            whileHover={{ scale: 1.05 }}
                          >
                            <Building className="w-3 h-3 mr-2" />
                            {client.name}
                            <motion.button
                              type="button"
                              onClick={() => toggleClientSelection(client)}
                              className="ml-2 text-green-600 hover:text-green-800 focus:outline-none"
                              whileHover={{ scale: 1.2 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <X className="w-3 h-3" />
                            </motion.button>
                          </motion.span>
                        ))}
                      </motion.div>
                    )}
                  </div>
                </motion.div>

                {/* Section Upload CV */}
                <motion.div
                  className="space-y-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.3 }}
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="bg-orange-100 rounded-lg p-2">
                      <FileText className="w-5 h-5 text-orange-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Document CV</h3>
                  </div>

                  <motion.div
                    onClick={handleClick}
                    className={`relative border-2 border-dashed rounded-xl p-8 cursor-pointer transition-all duration-300 ${
                      selectedFile
                        ? 'border-green-300 bg-green-50'
                        : 'border-gray-300 hover:border-orange-400 hover:bg-orange-50'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex flex-col items-center justify-center space-y-4">
                      {selectedFile ? (
                        <>
                          <motion.div
                            className="bg-green-100 rounded-full p-3"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", duration: 0.5 }}
                          >
                            <Check className="w-8 h-8 text-green-600" />
                          </motion.div>
                          <div className="text-center">
                            <p className="text-green-800 font-medium">CV ajouté avec succès</p>
                            <p className="text-green-600 text-sm mt-1">{selectedFile.name}</p>
                          </div>
                        </>
                      ) : (
                        <>
                          <motion.div
                            className="bg-orange-100 rounded-full p-3"
                            whileHover={{ rotate: 5 }}
                          >
                            <Upload className="w-8 h-8 text-orange-600" />
                          </motion.div>
                          <div className="text-center">
                            <p className="text-gray-700 font-medium">Cliquez pour ajouter un CV</p>
                            <p className="text-gray-500 text-sm mt-1">Format PDF uniquement</p>
                          </div>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      onChange={handleFileChange}
                      className="hidden"
                      ref={inputFileRef}
                      accept=".pdf"
                    />
                  </motion.div>
                </motion.div>

                {/* Section Préférences professionnelles */}
                <motion.div
                  className="space-y-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.85, duration: 0.3 }}
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="bg-indigo-100 rounded-lg p-2">
                      <Briefcase className="w-5 h-5 text-indigo-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Préférences professionnelles</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Type de contrat */}
                    <div className="space-y-2">
                      <Label htmlFor="contratType" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                        <FileText className="w-4 h-4 text-gray-500" />
                        <span>Type de contrat souhaité</span>
                      </Label>
                      <select
                        id="contratType"
                        value={formData.contratType}
                        onChange={(e) => setFormData({ ...formData, contratType: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-indigo-500 focus:ring-indigo-500 transition-all duration-200"
                      >
                        <option value="">Sélectionner un type</option>
                        <option value="Intérim">Intérim</option>
                        <option value="CDD">CDD</option>
                        <option value="CDI">CDI</option>
                        <option value="Freelance">Freelance</option>
                      </select>
                    </div>

                    {/* Statut */}
                    <div className="space-y-2">
                      <Label htmlFor="status" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <span>Statut</span>
                      </Label>
                      <select
                        id="status"
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-indigo-500 focus:ring-indigo-500 transition-all duration-200"
                      >
                        <option value="Disponible">Disponible</option>
                        <option value="En mission">En mission</option>
                        <option value="Indisponible">Indisponible</option>
                        <option value="En recherche">En recherche</option>
                      </select>
                    </div>

                    {/* Distance de déplacement */}
                    <div className="space-y-2">
                      <Label htmlFor="distance" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                        <Car className="w-4 h-4 text-gray-500" />
                        <span>Distance de déplacement (km)</span>
                      </Label>
                      <Input
                        id="distance"
                        type="number"
                        value={formData.distance}
                        onChange={(e) => setFormData({ ...formData, distance: e.target.value })}
                        placeholder="50"
                        className="border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 rounded-lg transition-all duration-200"
                      />
                    </div>

                    {/* Salaire journalier */}
                    <div className="space-y-2">
                      <Label htmlFor="dailySalaryExpectations" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                        <DollarSign className="w-4 h-4 text-gray-500" />
                        <span>Salaire journalier souhaité (€)</span>
                      </Label>
                      <Input
                        id="dailySalaryExpectations"
                        type="number"
                        value={formData.dailySalaryExpectations}
                        onChange={(e) => setFormData({ ...formData, dailySalaryExpectations: e.target.value })}
                        placeholder="150"
                        className="border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 rounded-lg transition-all duration-200"
                      />
                    </div>
                  </div>

                  {/* Commentaire */}
                  <div className="space-y-2">
                    <Label htmlFor="comment" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      <span>Commentaire / Notes</span>
                    </Label>
                    <textarea
                      id="comment"
                      value={formData.comment}
                      onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                      placeholder="Informations complémentaires sur le candidat..."
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-indigo-500 focus:ring-indigo-500 transition-all duration-200 resize-none"
                    />
                  </div>
                </motion.div>

                {/* Boutons d'action */}
                <motion.div
                  className="flex justify-end space-x-4 pt-6 border-t border-gray-200"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9, duration: 0.3 }}
                >
                  <motion.button
                    type="button"
                    onClick={() => onOpenChange(false)}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Annuler
                  </motion.button>
                  <motion.button
                    type="submit"
                    disabled={loading}
                    className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    whileHover={{ scale: loading ? 1 : 1.02 }}
                    whileTap={{ scale: loading ? 1 : 0.98 }}
                  >
                    {loading ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                        />
                        <span>Création...</span>
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        <span>Créer le candidat</span>
                      </>
                    )}
                  </motion.button>
                </motion.div>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}


export default freshModal(CreateCandidateModalFormSession, "open");
